import { useLocation } from '@gatsbyjs/reach-router';
import React from 'react';
import { Helmet } from 'react-helmet';
import { createAbsoluteHref } from 'utils/routeUtils';

import useInitialCountryLocale from '@skippay/core/hooks/useInitialCountryLocale';
import useUserCountryRelatedInfo from '@skippay/core/hooks/useUserCountryRelatedInfo';
import { useIntl } from '@skippay/core/i18n/intl';

import { corporateContact, socialProfile, speakable, webpage } from './structuredData';

interface Props {
	description?: string;
	lang?: string;
	/** Tell web-crawlers to NOT include page in search results */
	noIndex?: boolean;
	noFollow?: boolean;
	noStructuredData?: boolean;
	noUrlMeta?: boolean;
	ogDescription?: string;
	ogLocale?: string;
	ogTitle?: string;
	title?: string;
	article?: {
		published?: string;
		modified?: string;
	};
	noReferrer?: boolean;
}

function SEO(props: Props): JSX.Element {
	const intl = useIntl();
	const location = useLocation();
	const { locale, territory } = useInitialCountryLocale();
	const { selfCareUrl } = useUserCountryRelatedInfo();

	// TODO: images from public web
	const OG_IMAGE_ABSOLUTE_URL = `${process.env.SELF_CARE_URL}/images/og/${locale}/image.png`;
	const OG_IMAGE_SQUARE_ABSOLUTE_URL = `${process.env.SELF_CARE_URL}/images/og/${locale}/image_square.png`;

	const TITLE_DEFAULT = intl.formatMessage({
		id: 'SEO.title',
		defaultMessage: 'Bezpečně nejlepší způsob placení',
	});

	const DESCIPTION_DEFAULT = intl.formatMessage({
		id: 'SEO.description',
		defaultMessage:
			'Plaťte na internetu přes Skip Pay. Zboží si nejprve doma v klidu vyzkoušíte a potom kartou či převodem zaplatíte. Platba po doručení je navíc zcela zdarma.',
	});

	const {
		description = DESCIPTION_DEFAULT,
		lang = locale,
		noIndex,
		noFollow,
		noUrlMeta,
		ogDescription = props.description || DESCIPTION_DEFAULT,
		ogLocale = territory,
		ogTitle = props.title || TITLE_DEFAULT,
		title = TITLE_DEFAULT,
		article,
		noReferrer,
	} = props;

	const robotsTag =
		noIndex && noFollow
			? 'noindex,nofollow'
			: noIndex
				? 'noindex'
				: noFollow
					? 'nofollow'
					: undefined;

	/* When prerendering registration screen, do not render og:url meta and link rel="canonical" tags
	 * Thats because screen can be shared via facebook and url may contain referral query parameter
	 * For more information see https://mallpay.atlassian.net/browse/SS-2047
	 */
	return (
		<Helmet htmlAttributes={{ lang }} title={title} titleTemplate="%s | Skip Pay">
			<meta name="description" content={description} />
			{robotsTag && <meta name="robots" content={robotsTag} />}
			{!noUrlMeta && <meta property="og:url" content={createAbsoluteHref(location, selfCareUrl)} />}
			{!noUrlMeta && (
				<link rel="canonical" href={createAbsoluteHref(location.pathname, selfCareUrl)} />
			)}

			{/* OpenGraph data */}
			<meta property="og:title" content={`${ogTitle} | Skip Pay`} />
			<meta property="og:type" content="website" />
			<meta property="og:locale" content={ogLocale} />
			{/* OpenGraph image for facebook etc, recommended aspect ratio 1.91:1 and minimal height 600px */}
			<meta property="og:image" itemProp="image" content={OG_IMAGE_ABSOLUTE_URL} />
			<meta property="og:image:secure_url" itemProp="image" content={OG_IMAGE_ABSOLUTE_URL} />
			<meta property="og:image:type" content="image/png" />
			<meta property="og:image:width" content="1200" />
			<meta property="og:image:height" content="630" />
			{/* OpenGraph image for WhatsApp, recommended aspect ratio 1:1 and height 300px */}
			<meta property="og:image" itemProp="image" content={OG_IMAGE_SQUARE_ABSOLUTE_URL} />
			<meta property="og:image:secure_url" content={OG_IMAGE_SQUARE_ABSOLUTE_URL} />
			<meta property="og:image:type" content="image/png" />
			<meta property="og:image:width" content="300" />
			<meta property="og:image:height" content="300" />
			{/* Various OpenGraph descriptions */}
			<meta property="og:description" content={ogDescription} />
			<meta property="og:image:alt" content={ogDescription} />
			<meta property="twitter:description" content={ogDescription} />
			{/* Twitter specific OpenGraph data */}
			<meta name="twitter:title" content={`${ogTitle} | Skip Pay`} />
			<meta name="twitter:card" content="summary_large_image" />
			<meta name="twitter:image" content={OG_IMAGE_ABSOLUTE_URL} />
			{/* Facebook specific OpenGraph data */}
			<meta property="fb:app_id" content="472607120493988" />
			{/* Structured data */}
			{!props.noStructuredData &&
				React.Children.toArray([
					<script type="application/ld+json">{JSON.stringify(corporateContact(locale))}</script>,
					<script type="application/ld+json">{JSON.stringify(socialProfile(locale))}</script>,
					<script type="application/ld+json">{JSON.stringify(speakable(locale))}</script>,
					<script type="application/ld+json">{JSON.stringify(webpage(locale))}</script>,
				])}

			{article?.published && <meta property="article:published_time" content={article.published} />}
			{article?.modified && <meta property="article:modified_time" content={article.modified} />}

			{noReferrer && <meta name="referrer" content="no-referrer" />}
		</Helmet>
	);
}

export default SEO;
